import { type CampaignSource, TOURNAMENTS_CAMPAIGN_TYPE } from '../../../types/campaign.types';
import { useTournamentLeaderboard } from '../useTournamentLeaderboard';

export interface TournamentCardLiveData {
  /** Current player's leaderboard position. */
  playerPosition?: number;
  /** Current player's leaderboard score. */
  playerScore?: number;
}

/**
 * Tournament card live data — the current player's leaderboard position/score,
 * polled while the card is active. Self-gates by type; `staleTime: 0` refetches
 * on (re)mount so re-entering fetches fresh data.
 */
export const useTournamentCardLiveData = (
  campaign: CampaignSource | null | undefined,
  isActive: boolean,
): TournamentCardLiveData => {
  const enabled = campaign?.type === TOURNAMENTS_CAMPAIGN_TYPE && isActive;
  const query = useTournamentLeaderboard(enabled ? campaign?.id ?? '' : '', {
    enabled,
    staleTime: 0,
  });
  const live = query.data?.player;

  // Seed Position/Score from the campaigns-list value (parseTournamentsData puts
  // the player's score on the campaign). This is why the first card showed a
  // position before: the list provides it instantly. The leaderboard poll then
  // refines it — but if a poll omits `position`, we keep the seed instead of
  // going blank. (No store sync; this is a pure read of the already-parsed list.)
  const seed = campaign && 'score' in campaign ? campaign.score : undefined;

  return {
    playerPosition: live?.position ?? seed?.position,
    playerScore: live?.score ?? seed?.score,
  };
};
